import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { useMemo, useState, useRef, useEffect } from "react";
import { getFormattedFieldValue } from "../../utils";
import type { RegistryType } from "@/schemas/digital-files/registry";
import type { Section } from "../../types";
import type { ProccesedExpedientValues } from "@/types/entities/templates";
import dayjs from "dayjs";

interface BadgesSectionProps {
  sections: Section[];
  expirations: Record<string, string>;
  processedValues: ProccesedExpedientValues;
  registry: RegistryType;
  onOpenModal: () => void;
}

export const BadgesSection = ({
  sections,
  expirations,
  processedValues,
  onOpenModal,
}: BadgesSectionProps) => {
  const [visibleBadgesCount, setVisibleBadgesCount] = useState<number>(0);
  const badgesContainerRef = useRef<HTMLDivElement>(null);

  const allBadges = useMemo(() => {
    return (
      sections
        //!!!!!!!!! hide "relaciones" section !!!!!!!!!
        ?.filter(
          (section) => section.title.toLowerCase().trim() !== "relaciones",
        )
        ?.flatMap((section) => section.fields || []) || []
    );
  }, [sections]);

  // Calcula cuántos badges se pueden mostrar en 3 líneas
  useEffect(() => {
    if (!badgesContainerRef.current || allBadges.length === 0) return;

    const containerWidth = badgesContainerRef.current.offsetWidth;
    const gap = 8; // gap-2 = 8px
    const buttonWidth = 70; // Ancho aproximado del botón "Ver más"
    const maxValueWidth = 300; // max-w-75 = 18.75rem = 300px

    // Medimos el ancho de cada badge
    let currentLineWidth = 0;
    let currentLine = 1;
    let count = 0;

    for (let i = 0; i < allBadges.length; i++) {
      const field = allBadges[i];
      const hasExpiration = expirations[field.id];

      // Cálculo aproximado del ancho del badge
      const fieldName = field.name || "";
      const fieldValue = processedValues[field.id]?.value
        ? getFormattedFieldValue(
            field.configuration?.type,
            processedValues[field.id].value,
            "No asignado",
          ).join(", ")
        : "No asignado";

      // Aproximadamente 8px por carácter + padding (24px) + espacio ": " + icono si hay expiración (24px)
      const labelWidth = fieldName.length * 8;
      const valueWidth = Math.min(
        fieldValue.toString().length * 8,
        maxValueWidth,
      );
      const padding = 24;
      const separator = 16; // ": "
      const expirationIcon = hasExpiration ? 24 : 0;

      const estimatedWidth =
        labelWidth + valueWidth + padding + separator + expirationIcon;

      // Verificamos si cabe en la línea actual
      if (
        currentLineWidth +
          estimatedWidth +
          (count > 0 ? gap : 0) +
          buttonWidth >
        containerWidth
      ) {
        currentLine++;
        currentLineWidth = estimatedWidth;

        // Si ya tenemos 3 líneas, paramos
        if (currentLine > 3) {
          break;
        }
      } else {
        currentLineWidth += estimatedWidth + (count > 0 ? gap : 0);
      }

      count++;
    }

    setVisibleBadgesCount(count);
  }, [allBadges, expirations, processedValues]);

  return (
    <div
      ref={badgesContainerRef}
      className="flex gap-2 flex-wrap [--badge-height:--spacing(7)] [--badge-py:--spacing(0.5)]"
    >
      {allBadges.slice(0, visibleBadgesCount).map((field) => (
        <div key={field.id} className="flex">
          {expirations[field.id] &&
            dayjs(expirations[field.id]).isValid() &&
            dayjs().isBefore(dayjs(expirations[field.id])) && (
              <Badge
                variant="warning"
                style={{
                  borderRadius: "6px 0 0 6px",
                }}
              >
                <Icon type="far" name="clock" />
              </Badge>
            )}
          <Badge
            className="h-(--badge-height) py-(--badge-py)"
            style={
              expirations[field.id] &&
              dayjs(expirations[field.id]).isValid() &&
              dayjs().isBefore(dayjs(expirations[field.id]))
                ? {
                    borderRadius: "0 6px 6px 0",
                    borderLeft: "0",
                  }
                : {}
            }
          >
            {field.name}:{" "}
            {processedValues[field.id]?.value &&
            processedValues[field.id].value.length > 0 ? (
              <span className="text-body-loud max-w-75 truncate">
                {getFormattedFieldValue(
                  field.configuration?.type,
                  processedValues[field.id].value,
                  "No asignado",
                ).join(", ")}
              </span>
            ) : (
              "No asignado"
            )}
          </Badge>
        </div>
      ))}
      <Button
        variant="tertiary"
        size="xs"
        onClick={onOpenModal}
        className="h-(--badge-height) py-(--badge-py)"
      >
        Ver más
      </Button>
    </div>
  );
};
